import { Component, inject, OnInit, AfterViewInit, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as echarts from 'echarts';
import { ActivatedRoute, Router } from '@angular/router';
import { AppStateService } from '../../services/app-state.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Web3Service } from '../../services/web3.service';
import { ExternalLiquidityService } from '../../services/external-liquidity.service';

@Component({
  selector: 'app-pool-details',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './pool-details.component.html'
})
export class PoolDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chartContainer') chartContainer!: ElementRef;
  private chartInstance: echarts.ECharts | null = null;
  private resizeObserver: ResizeObserver | null = null;

  public state = inject(AppStateService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);
  private cdr = inject(ChangeDetectorRef);
  private web3Service = inject(Web3Service);
  private externalService = inject(ExternalLiquidityService);

  public Math = Math;

  private initialNetwork: string | null = null;

  constructor() {
    effect(() => {
      const currentNetwork = this.state.selectedNetworkId();
      untracked(() => {
        if (this.initialNetwork === null) {
          this.initialNetwork = currentNetwork;
        } else if (this.initialNetwork !== currentNetwork) {
          this.router.navigate(['/liquidity']);
        }
      });
    });
  }

  poolId: string = '';
  poolName: string = 'Loading...';
  token1: string = '';
  token2: string = '';
  token1Color: string = '#627EEA';
  token2Color: string = '#2775CA';
  token1Logo: string = '';
  token2Logo: string = '';

  // Mock specific data
  tvl: string = '$0.00';
  volume24h: string = '$0.00';
  fees24h: string = '$0.00';
  apr: string = '0.00%';
  currentPrice: string = 'Loading...';
  address: string = '';
  protocolFee: string = '0.00%';

  tvlRaw: number = 0;
  volumeRaw: number = 0;
  feesRaw: number = 0;
  aprRaw: number = 0;

  activeTimeFilter: string = '30D';
  timeFilters = ['7D', '30D', '90D', '1Y', 'All'];

  activeMetric: string = 'TVL';
  metrics = ['TVL', 'Volume', 'Fees', 'APR'];

  // Widget State (Removing but keeping selectedFeeTier for reference if needed)
  selectedFeeTier: string = '0.05%';

  // Content Tabs State
  activeContentTab: string = 'Overview';
  contentTabs = ['Overview', 'Transactions']; // 'Holders', 'Analytics' commented out per request

  // Chart Mocks
  chartPoints: string = '';
  chartArea: string = '';

  isWatchlisted: boolean = false;

  toggleWatchlist() {
    this.isWatchlisted = !this.isWatchlisted;
    if (this.isWatchlisted) {
      this.state.triggerNotification(`Added ${this.poolName} to Watchlist`, true);
    } else {
      this.state.triggerNotification(`Removed ${this.poolName} from Watchlist`, false);
    }
  }

  copyLink() {
    navigator.clipboard.writeText(window.location.href);
    this.state.triggerNotification('Pool link copied to clipboard', true);
  }

  setTimeFilter(filter: string) {
    this.activeTimeFilter = filter;
    this.renderChart();
  }

  setMetric(metric: string) {
    this.activeMetric = metric;
    this.renderChart();
  }

  ngAfterViewInit() {
    if (this.chartContainer) {
      this.chartInstance = echarts.init(this.chartContainer.nativeElement);
      this.renderChart();

      this.resizeObserver = new ResizeObserver(() => {
        this.chartInstance?.resize();
      });
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver && this.chartContainer) {
      this.resizeObserver.unobserve(this.chartContainer.nativeElement);
    }
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  }

  renderChart() {
    if (!this.chartInstance) return;

    let option: echarts.EChartsOption = {};

    if (this.activeMetric === 'TVL') {
      const startVal = this.tvlRaw || 0;
      const data = this.generateTimeSeriesData(startVal, startVal * 0.05);
      option = {
        grid: { left: 10, right: 10, top: 20, bottom: 20 },
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: data.map(d => d.time), show: false },
        yAxis: { type: 'value', min: 'dataMin', show: false },
        series: [{
          data: data.map(d => d.value),
          type: 'line',
          smooth: true,
          showSymbol: false,
          lineStyle: { color: '#627EEA', width: 3 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(98,126,234,0.3)' },
              { offset: 1, color: 'rgba(98,126,234,0.0)' }
            ])
          }
        }]
      };
    } else if (this.activeMetric === 'Volume') {
      const startVal = this.volumeRaw || 0;
      const data = this.generateTimeSeriesData(startVal, startVal * 0.1);
      option = {
        grid: { left: 10, right: 10, top: 20, bottom: 20 },
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: data.map(d => d.time), show: false },
        yAxis: { type: 'value', show: false },
        series: [{
          data: data.map(d => d.value),
          type: 'bar',
          itemStyle: { color: 'rgba(245,183,59,0.7)', borderRadius: [2, 2, 0, 0] }
        }]
      };
    } else if (this.activeMetric === 'Fees') {
      const startVal = this.feesRaw || 0;
      const data = this.generateTimeSeriesData(startVal, startVal * 0.1);
      option = {
        grid: { left: 10, right: 10, top: 20, bottom: 20 },
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: data.map(d => d.time), show: false },
        yAxis: { type: 'value', show: false },
        series: [{
          data: data.map(d => d.value),
          type: 'bar',
          itemStyle: { color: 'rgba(32,212,137,0.7)', borderRadius: [2, 2, 0, 0] }
        }]
      };
    } else if (this.activeMetric === 'APR') {
      const startVal = this.aprRaw || 0;
      const data = this.generateTimeSeriesData(startVal, startVal * 0.1);
      option = {
        grid: { left: 10, right: 10, top: 20, bottom: 20 },
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: data.map(d => d.time), show: false },
        yAxis: { type: 'value', min: 'dataMin', show: false },
        series: [{
          data: data.map(d => d.value),
          type: 'line',
          smooth: true,
          showSymbol: false,
          lineStyle: { color: '#F5B73B', width: 3 },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(245,183,59,0.3)' },
              { offset: 1, color: 'rgba(245,183,59,0.0)' }
            ])
          }
        }]
      };
    }

    this.chartInstance.setOption(option, true);
  }

  generateTimeSeriesData(startVal: number, volatility: number) {
    let data = [];
    let val = startVal;
    let time = new Date();

    let points = 60;
    let stepMinutes = 1;
    let formatOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };

    switch (this.activeTimeFilter) {
      case '1H':
        points = 60; stepMinutes = 1;
        break;
      case '1D':
        points = 96; stepMinutes = 15;
        break;
      case '1W':
        points = 168; stepMinutes = 60;
        formatOptions = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        break;
      case '1M':
        points = 120; stepMinutes = 6 * 60;
        formatOptions = { month: 'short', day: 'numeric' };
        break;
      case '1Y':
        points = 365; stepMinutes = 24 * 60;
        formatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
        break;
      case 'ALL':
        points = 365 * 3; stepMinutes = 24 * 60;
        formatOptions = { month: 'short', year: 'numeric' };
        break;
    }

    // Go back in time
    time.setMinutes(time.getMinutes() - (points * stepMinutes));

    for (let i = 0; i < points; i++) {
      val += (Math.random() - 0.48) * volatility; // slight upward bias for mock data
      time.setMinutes(time.getMinutes() + stepMinutes);
      data.push({
        time: time.toLocaleString('en-US', formatOptions),
        value: Math.max(0, val)
      });
    }
    return data;
  }

  generateDepthData(startPrice: number, endPrice: number, isBid: boolean) {
    let data = [];
    let cumulative = 0;
    const step = (endPrice - startPrice) / 30;
    for (let i = 0; i <= 30; i++) {
      const price = startPrice + (step * i);
      cumulative += Math.random() * 50;
      data.push([price, cumulative]);
    }
    return data;
  }

  // Transaction Mocks
  transactions: any[] = [];
  currentPage: number = 1;
  totalPages: number = 1;
  totalRecords: number = 0;
  itemsPerPage: number = 10;

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.poolId = id;
        this.fetchPoolData(id);
      }
    });
  }

  async fetchPoolData(poolAddress: string) {
    if (poolAddress.includes('-')) return;

    try {
      const poolsRes = await this.externalService.getPools(1, 1, poolAddress);
      const pool = poolsRes.data.length > 0 ? poolsRes.data[0] : undefined;

      if (pool) {
        this.token1 = pool.token0.symbol;
        this.token2 = pool.token1.symbol;
        this.poolName = `${this.token1} / ${this.token2}`;
        this.token1Color = this.getColor(this.token1);
        this.token2Color = this.getColor(this.token2);

        this.tvl = `$${(pool.tvlUsd || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
        this.volume24h = `$${(pool.volume24h || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
        
        const feeTierNum = pool.feeTier ? pool.feeTier / 1000000 : 0.003;
        const feeVal = (pool.volume24h || 0) * feeTierNum;
        this.fees24h = `$${feeVal.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
        
        this.apr = `${(pool.apr || 0).toFixed(2)}%`;
        
        this.tvlRaw = pool.tvlUsd || 0;
        this.volumeRaw = pool.volume24h || 0;
        this.feesRaw = feeVal;
        this.aprRaw = pool.apr || 0;
        
        this.protocolFee = pool.feeTier ? `${(pool.feeTier / 10000)}%` : '0.3%';
        this.address = `${pool.address.slice(0, 6)}...${pool.address.slice(-4)}`;
        
        const tokensRes0 = await this.externalService.getTokens(1, 1, pool.token0.symbol);
        const tokensRes1 = await this.externalService.getTokens(1, 1, pool.token1.symbol);
        
        const t0 = tokensRes0.data.length > 0 ? tokensRes0.data[0] : null;
        const t1 = tokensRes1.data.length > 0 ? tokensRes1.data[0] : null;
        
        if (t0) this.token1Logo = t0.logoUrl;
        if (t1) this.token2Logo = t1.logoUrl;
        
        if (t0 && t1 && t1.priceUsd > 0) {
            const ratio = t0.priceUsd / t1.priceUsd;
            this.currentPrice = `1 ${this.token1} = ${ratio.toFixed(6)} ${this.token2}`;
        } else {
            this.currentPrice = `Loading...`;
        }

        this.renderChart();
        this.cdr.detectChanges();
      } else {
        // Fallback to internal pool API if not found in external
        this.http.get<any>(`${environment.apiUrl}/user/pool/${poolAddress}`).subscribe({
          next: (res) => {
            if (res.status === 'success' && res.data) {
              const d = res.data;
              this.token1 = d.token0.symbol;
              this.token2 = d.token1.symbol;
              this.poolName = `${this.token1} / ${this.token2}`;
              this.token1Color = this.getColor(this.token1);
              this.token2Color = this.getColor(this.token2);

              this.tvl = `$${d.tvlUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
              this.volume24h = `$${d.volume24hUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
              this.fees24h = `$${d.fees24hUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
              this.apr = `${d.apr.toFixed(2)}%`;
              
              this.tvlRaw = d.tvlUsd;
              this.volumeRaw = d.volume24hUsd;
              this.feesRaw = d.fees24hUsd;
              this.aprRaw = d.apr;
              
              this.protocolFee = d.feeTier;
              this.address = `${d.poolAddress.slice(0, 6)}...${d.poolAddress.slice(-4)}`;
              this.currentPrice = `1 ${this.token1} = ${d.currentPrice} ${this.token2}`;
              
              this.fetchLivePrice(poolAddress, d.token0.decimals, d.token1.decimals);
              this.renderChart();
              this.cdr.detectChanges();
            }
          },
          error: (err) => console.error(err)
        });
      }
    } catch(err) {
      console.error('Error fetching pool data:', err);
    }

    this.loadTransactions(1);
  }

  async fetchLivePrice(poolAddress: string, decimals0: number, decimals1: number) {
    try {
      const abi = ['function slot0() external view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint8 feeProtocol, bool unlocked)'];
      const poolContract = await this.web3Service.getReadContract(poolAddress, abi);
      const slot0 = await poolContract['slot0']();
      const sqrtPriceX96 = slot0.sqrtPriceX96;
      
      if (sqrtPriceX96 > 0n) {
        const Q96 = 2n ** 96n;
        const priceX192 = sqrtPriceX96 * sqrtPriceX96;
        const price = Number(priceX192) / Number(Q96 * Q96);
        const decimalAdjustment = 10 ** (decimals0 - decimals1);
        const adjustedPrice = price * decimalAdjustment;
        
        this.currentPrice = `1 ${this.token1} = ${adjustedPrice.toFixed(6)} ${this.token2}`;
        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error('Failed to fetch live slot0', error);
    }
  }

  loadTransactions(page: number) {
    if (!this.poolId || this.poolId.includes('-')) return;

    this.http.get<any>(`${environment.apiUrl}/user/pool/${this.poolId}/transactions?page=${page}&limit=${this.itemsPerPage}`).subscribe({
      next: (res) => {
        if (res.status === 'success' && res.data) {
          this.currentPage = res.data.pagination.page;
          this.totalRecords = res.data.pagination.total;
          this.totalPages = Math.ceil(this.totalRecords / this.itemsPerPage) || 1;
          
          this.transactions = res.data.transactions.map((t: any) => {
            const timeDiff = Math.floor(Date.now() / 1000) - t.timestamp;
            let timeStr = 'just now';
            if (timeDiff > 86400) timeStr = `${Math.floor(timeDiff / 86400)}d`;
            else if (timeDiff > 3600) timeStr = `${Math.floor(timeDiff / 3600)}h`;
            else if (timeDiff > 60) timeStr = `${Math.floor(timeDiff / 60)}m`;

            return {
              time: timeStr,
              type: t.type,
              usd: `$${(t.usdValue || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
              t1: t.token0Amount.toFixed(4),
              t2: t.token1Amount.toFixed(4),
              wallet: `${t.walletAddress.slice(0, 6)}...${t.walletAddress.slice(-4)}`,
              typeColor: t.type === 'Add' ? 'text-emerald-400' : (t.type === 'Remove' ? 'text-red-400' : 'text-blue-400')
            };
          });
          this.cdr.detectChanges();
        }
      },
      error: (e) => console.error('Failed to fetch transactions', e)
    });
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.loadTransactions(this.currentPage + 1);
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.loadTransactions(this.currentPage - 1);
    }
  }

  goToPage(page: number | string) {
    if (typeof page === 'number') {
      this.loadTransactions(page);
    }
  }

  get paginationPages() {
    const pages = [];
    const total = this.totalPages;

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      if (this.currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', total);
      } else if (this.currentPage >= total - 3) {
        pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
      } else {
        pages.push(1, '...', this.currentPage - 1, this.currentPage, this.currentPage + 1, '...', total);
      }
    }
    return pages;
  }

  getColor(token: string): string {
    const t = token.toUpperCase();
    if (t === 'ETH') return '#627EEA';
    if (t === 'USDC') return '#2775CA';
    if (t === 'BTC' || t === 'WBTC') return '#F7931A';
    if (t === 'BNB') return '#F3BA2F';
    if (t === 'DXOR') return '#F5B73B';
    if (t === 'DAI') return '#F5AC37';
    if (t === 'UNI') return '#FF007A';
    if (t === 'LINK') return '#2A5ADA';
    return '#888888';
  }

  goBack() {
    this.router.navigate(['/liquidity']);
  }

  goToAddLiquidity() {
    this.router.navigate(['/liquidity/new'], { queryParams: { token1: this.token1, token2: this.token2 } });
  }

  goToSwap() {
    this.router.navigate(['/swap'], { queryParams: { from: this.token1, to: this.token2 } });
  }
}
